import { defineStore } from 'pinia'
import axios from 'axios'
import { ApiKeysV2Service } from '@/services/api-keys-v2-service'
import type { ApiError } from '@/utils/types'
import type {
  ApiKeyResponse,
  ApiKeysResponse,
  ApiKeyType,
  ApiKeyUpdate,
} from '@/services/api-keys-v2-service/types'
import { FALLBACK_API_ERROR } from '@/utils/consts'
import { ref } from 'vue'

type ActionFlag = 'save' | 'fetch' | 'delete' | 'setDuplicate'

export const useApiKeysStore = defineStore('api-keys', () => {
  const apiKeys = ref([] as ApiKeyResponse[])
  const prevResponse = ref({} as ApiKeysResponse)
  const whiteListIp = ref(null as string | null)
  const loading = ref(false)
  const putting = ref(false)
  const error = ref({} as Record<ActionFlag, ApiError | null>)

  const updateProfileDuplicate = async (
    exchange: string,
    profile: number,
    apiKeyType: ApiKeyType,
    duplicateId: number | null,
  ) => {
    try {
      await ApiKeysV2Service.setDuplicate(exchange, profile, apiKeyType ?? 'cex_cex', duplicateId)
      error.value.setDuplicate = null
    } catch (thrown) {
      if (axios.isAxiosError(thrown) && thrown.response) {
        error.value.setDuplicate = {
          message: thrown.response?.data,
          code: thrown.response?.status,
        }
      } else {
        error.value.setDuplicate = FALLBACK_API_ERROR
      }
    }
  }
  const fetchKeys = async (forceReload = false): Promise<ApiKeysResponse | undefined> => {
    if ((!forceReload && apiKeys.value?.length) ?? 0 > 0) {
      return prevResponse.value
    }
    loading.value = true
    try {
      const req = await ApiKeysV2Service.fetchApiKeys()
      prevResponse.value = req.data
      // fix nulled key type
      const data = req?.data.api_keys.map((x) => {
        if (x.key_type == null) {
          x.key_type = 'cex_cex'
        }
        return x
      })
      apiKeys.value = data

      whiteListIp.value = req?.data.proxy
      error.value.fetch = null

      return req?.data
    } catch (thrown) {
      if (axios.isAxiosError(thrown) && thrown.response) {
        error.value.fetch = {
          message: thrown.response?.data,
          code: thrown.response?.status,
        }
      } else {
        error.value.fetch = FALLBACK_API_ERROR
      }
    } finally {
      loading.value = false
    }
  }
  const saveKey = async (
    exchange: string,
    profile: number,
    apiKeyType: ApiKeyType | null,
    data: Record<string, string | null | number>,
  ) => {
    const fmtData = {
      api_key: data['apiKey'] ?? null,
      secret: data['secret'] ?? null,
      passphrase: data['password'] ?? null,
      proxy: data['proxy'] ?? null,
      extra: {
        useragent: data['userAgent'] ?? null,
        token: data['webToken'] ?? null,
        fingerprint: data['fingerPrint'] ?? null,
        device_id: data['deviceId'] ?? null,
        platform_id: data['platformId'] ?? null,
        app_version: data['appVersion'] ?? null,
        anti_device_id: data['antiDeviceId'] ?? null,
        app_id: data['appId'] ?? null,
        device_brand: data['deviceBrand'] ?? null,
        user_id: data['userId'] ?? null,
        evm_address: data['evmAddress'] ?? null,
        sol_address: data['solAddress'] ?? null,
      },
    } as ApiKeyUpdate
    if (exchange == 'ourbit') {
      fmtData['api_key'] = 'not_required'
      fmtData['secret'] = 'not_required'
    } else if (exchange == '1inch') {
      fmtData['secret'] = 'not_required'
    }
    try {
      const req = await ApiKeysV2Service.saveApiKey(
        exchange,
        profile,
        apiKeyType ?? 'cex_cex',
        fmtData,
      )
      error.value.save = null
      return req?.data
    } catch (thrown) {
      if (axios.isAxiosError(thrown) && thrown.response) {
        error.value.save = {
          message: thrown.response?.data,
          code: thrown.response?.status,
        }
      } else {
        error.value.save = FALLBACK_API_ERROR
      }
    }
  }

  const deleteKey = async (exchange: string, profile: number, apiKeyType: ApiKeyType | null) => {
    try {
      const req = await ApiKeysV2Service.deleteApiKey(exchange, profile, apiKeyType ?? 'cex_cex')
      error.value.delete = null
      return req?.data
    } catch (thrown) {
      if (axios.isAxiosError(thrown) && thrown.response) {
        error.value.delete = {
          message: thrown.response?.data,
          code: thrown.response?.status,
        }
      } else {
        error.value.delete = FALLBACK_API_ERROR
      }
    }
  }

  return {
    apiKeys,
    prevResponse,
    whiteListIp,
    loading,
    putting,
    error,
    updateProfileDuplicate,
    fetchKeys,
    saveKey,
    deleteKey,
  }
})
